import { createInstance, Identify } from '@amplitude/analytics-browser';
import { ADDON_AMPLITUDE_API_KEY, ADDON_COOKIE_DOMAIN, ADDON_PRODUCT, isAddonEnabled } from './config';
import { maskCallPath, maskCallPathsIn } from './roomHash';

type TProps = Record<string, string | number | boolean | undefined>;

// Метки ссылки снимаются в момент загрузки модуля: Meet сразу перенаправляет на /call,
// и к старту SDK параметров в адресе уже нет — источник терялся (проверено 18.10).
const ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
const landingAttribution: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(location.search);
    ATTR_KEYS.forEach(k => { const v = params.get(k); if (v) out[k] = v.slice(0, 120); });
  } catch (e) { /* noop */ }
  return out;
})();

// Если к старту SDK меток в адресе уже нет, а при загрузке были — отдаём их сами.
const restoreAttribution = (inst: ReturnType<typeof createInstance>): void => {
  try {
    if (!Object.keys(landingAttribution).length) return;
    // SDK уже мог записать initial_utm_* = EMPTY, setOnce его не перезапишет —
    // поэтому своё событие и отдельные свойства landing_utm_* (первое касание).
    const id = new Identify();
    Object.entries(landingAttribution).forEach(([k, v]) => { id.setOnce(`landing_${k}`, v); });
    inst.identify(id);
    inst.track('landing_attributed', { ...landingAttribution, product: ADDON_PRODUCT });
  } catch (e) { /* noop */ }
};

// Ключи, которые никогда не уходят в наш проект (персональные данные).
const FORBIDDEN_KEY = /e-?mail|name|google|token|text|phrase|message|transcript|caption/i;
// Имя комнаты — секрет приглашения: ключи вида room_id / roomId / room / url не уходят никогда.
const ROOM_KEY = /^(room|room_?id|url)$/i;
const looksLikeEmail = (v: unknown) => typeof v === 'string' && v.indexOf('@') >= 0;

export const sanitizeProps = (props?: Record<string, unknown>): TProps => {
  const out: TProps = {};
  if (!props) return out;
  Object.keys(props).forEach(key => {
    if (key === 'userId' || key === 'user_id') return;
    if (FORBIDDEN_KEY.test(key)) return;
    if (ROOM_KEY.test(key)) return;
    const v = props[key];
    if (looksLikeEmail(v)) return;
    if (typeof v === 'string') out[key] = maskCallPath(v) as string;
    else if (typeof v === 'number' || typeof v === 'boolean') out[key] = v;
  });
  return out;
};

// client_id GA4 из cookie `_ga` вида GA1.1.<n>.<n> — последние две части.
export const parseGaClientId = (cookieString?: string | null): string | null => {
  const m = /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)(?:;|$)/.exec(cookieString || '');
  return m ? m[1] : null;
};

// Общий ключ GA4 <-> Amplitude: ga_client_id уходит в Amplitude, device_id Amplitude — в GA4.
let gaLinked = false;
let ampLinked = false;
const linkIds = (inst: ReturnType<typeof createInstance>): void => {
  try {
    if (!gaLinked) {
      const clientId = parseGaClientId(document.cookie);
      if (clientId) {
        const id = new Identify();
        id.set('ga_client_id', clientId);
        inst.identify(id);
        gaLinked = true;
      }
    }
  } catch (e) { /* noop */ }
  try {
    if (!ampLinked) {
      const gtag = (window as any).gtag;
      const deviceId = inst.getDeviceId();
      if (typeof gtag === 'function' && deviceId) {
        gtag('set', 'user_properties', { amp_device_id: deviceId });
        gtag('event', 'amp_link', {});
        ampLinked = true;
      }
    }
  } catch (e) { /* noop */ }
};
const scheduleLinkIds = (inst: ReturnType<typeof createInstance>): void => {
  try {
    linkIds(inst);
    [3000, 10000].forEach(ms => { setTimeout(() => linkIds(inst), ms); });
  } catch (e) { /* noop */ }
};

let instance: ReturnType<typeof createInstance> | null = null;
let failed = false;

const getInstance = () => {
  if (instance || failed) return instance;
  try {
    if (!isAddonEnabled()) return null;
    const inst = createInstance();
    const options: any = {
      includeUtm: true,
      includeReferrer: true,
      includeGclid: true,
      includeFbclid: true,
      // Анонимный посетитель Meet не порождает событий EventsRouter, поэтому
      // просмотры и сессии собирает сам SDK — иначе такого человека не видно вовсе.
      defaultTracking: { attribution: true, pageViews: true, sessions: true, formInteractions: false, fileDownloads: false },
      cookieOptions: { domain: ADDON_COOKIE_DOMAIN },
    };
    const deviceId = new URL(location.href).searchParams.get('amp_device_id');
    if (deviceId) {
      options.deviceIdFromUrlParam = true;
      options.deviceId = deviceId;
    }
    // Автособытия SDK (просмотр страницы /call/<комната>, атрибуция) несут адрес звонка —
    // до отправки он заменяется на /call/:room.
    try {
      inst.add({
        name: 'lm-mask-room',
        type: 'enrichment',
        setup: async () => undefined,
        execute: async (event: any) => {
          try {
            if (event?.event_properties) event.event_properties = maskCallPathsIn(event.event_properties);
            if (event?.user_properties) event.user_properties = maskCallPathsIn(event.user_properties);
          } catch (e) { /* noop */ }
          return event;
        },
      } as any);
    } catch (e) { /* noop */ }
    inst.init(ADDON_AMPLITUDE_API_KEY, undefined, options);
    restoreAttribution(inst);
    scheduleLinkIds(inst);
    instance = inst;
  } catch (e) {
    failed = true;
    instance = null;
  }
  return instance;
};

// Запуск сразу при загрузке приложения, а не по первому событию (проверено на проде 19.10:
// у анонимного посетителя экземпляр так и не поднимался).
export const addonInit = (): void => {
  try { getInstance(); } catch (e) { /* аддон не должен ничего ломать */ }
};

export const addonSetUserId = (userId: string): void => {
  try {
    const inst = getInstance();
    if (inst && userId) inst.setUserId(userId);
  } catch (e) { /* аддон не должен ничего ломать */ }
};

// Общий ключ аккаунта между продуктами: SHA-256 от email в нижнем регистре.
// Сам email в Amplitude не уходит; identify — один раз на смену пользователя.
let lastAccountHash: string | null = null;
export const accountKeyHash = async (email: string): Promise<string | null> => {
  const norm = String(email || '').trim().toLowerCase();
  if (!norm) return null;
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(norm));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const addonSetAccount = (email?: string | null): void => {
  try {
    if (!email) return;
    const inst = getInstance();
    if (!inst) return;
    accountKeyHash(email).then(hash => {
      try {
        if (!hash || hash === lastAccountHash) return;
        lastAccountHash = hash;
        const id = new Identify();
        id.set('lm_account', hash);
        inst.identify(id);
      } catch (e) { /* noop */ }
    }).catch(() => { /* noop */ });
  } catch (e) { /* аддон не должен ничего ломать */ }
};

export const addonTrack = (eventName: string, props?: Record<string, unknown>, time?: number): void => {
  try {
    const inst = getInstance();
    if (!inst) return;
    const eventProps: any = { ...sanitizeProps(props), product: ADDON_PRODUCT };
    // время — опцией события, а не свойством: иначе Amplitude примет его за обычное поле
    inst.track(eventName, eventProps, time ? { time } : undefined);
  } catch (e) { /* аддон не должен ничего ломать */ }
};
